<div class="presets-section limited-width">
  @if (initialVisit) {
    <div class="preset-options-header">
      <h1 class="gradient-title">Welcome to {{ appName }}</h1>
      <p>
        Welcome to {{ appName }}, the <strong>custom loot filter generator for {{ gameName }}</strong>.
      </p>

      <p class="extra-info link-game-text">
        @if (poe1) {
          <img src="/img/logo-poe2-small.webp" class="link-game-logo"/>
          Looking for a <strong>Path of Exile 2</strong> filter?
          Visit <a href="https://poe2filter.com" (click)="$event.stopPropagation()">poe2filter.com</a> instead.
        } @else {
          <img src="/img/logo-poe1-small.webp" class="link-game-logo"/>
          Looking for a <strong>Path of Exile 1</strong> filter?
          Visit <a href="https://poe1filter.com" (click)="$event.stopPropagation()">poe1filter.com</a> instead.
        }
      </p>
    </div>
    <div class="section-divider"></div>
  }

  <div class="preset-options-header" [class.first]="!initialVisit">
    <h1 class="gradient-title">Select where you are at in the game</h1>
    <p class="extra-info">
      This will set several Quick Filters and an appropriate Tiering Mode for {{ poe1 ? 'Currency, Uniques and Divination Cards.' : 'Currency and Uniques.' }}
    </p>
  </div>

  <div class="preset-options">
    @for (p of presetsService.progressPresets; track p.key) {
      <div class="preset-option" [class.selected]="filter.progressPreset === p.key" (click)="setPreset(p.key)" (mouseenter)="showPresetTooltip(p.key)" (mouseleave)="hidePresetTooltip()">
        <div>
          <h3>{{ p.label }}</h3>
          <div class="extra-info">Level {{ p.level }}</div>
        </div>
        <div class="preset-image-wrap">
          <img src="{{ imgPrefix }}/img/presets/{{ p.img }}"/>
          <button type="button" class="preset-preview-btn" title="Preview changes" (click)="previewProgressPreset(p.key); $event.stopPropagation()">
            <span class="icon icon-zoom-in"></span>
          </button>
        </div>
      </div>
    }
  </div>

  <div class="section-row">
    <span>
      Game Mode & Economy
      <app-tooltip-wrapper><span class="extra-info">(?)</span>
        <ng-template #tooltip>
          Our filters divide Currency, Uniques and other items into 'Drop Tiers', based on their value. Items have different values in different game modes — called '<strong>Economies</strong>'. These settings determine which Economy is used.
          <br /><br />You can review and fine-tune the tiers and even create your own Custom Tiering Mode on the <a href="/tier-list/currency">Currency Tier List</a> and <a href="/tier-list/uniques">Uniques Tier List</a> tabs.
          <br /><br />Unless you're playing Solo-Self-Found (SSF), we will automatically update Currency values every hour, and Uniques values every day. You can configure this behaviour on the <a href="/settings">Settings</a> tab.
        </ng-template>
      </app-tooltip-wrapper>
    </span>

    <div class="title-toggle-btn" (click)="filter.league = !filter.league; updateEconomy()">
      <span class="input-group-btn">
        <button class="btn toggle-btn" [class.off]="!filter.league"></button>
      </span>
      <span class="hide-on-md">{{ leagueName }}&nbsp;</span>League
      <app-tooltip-wrapper><span class="extra-info">(?)</span>
        <ng-template #tooltip>
          Deselect if you're playing Standard or Hardcore Standard.
        </ng-template>
      </app-tooltip-wrapper>
    </div>

    <div class="title-toggle-btn" (click)="filter.hardcore = !filter.hardcore; updateEconomy()">
      <span class="input-group-btn">
        <button class="btn toggle-btn" [class.off]="!filter.hardcore"></button>
      </span>
      Hardcore
      <app-tooltip-wrapper><span class="extra-info">(?)</span>
        <ng-template #tooltip>
          Uses the {{ filter.league ? leagueName : 'Standard'}} Hardcore economy.
        </ng-template>
      </app-tooltip-wrapper>
    </div>

    <div class="title-toggle-btn" (click)="toggleSsf()">
      <span class="input-group-btn">
        <button class="btn toggle-btn" [class.off]="!filter.ssf"></button>
      </span>
      Solo-Self-Found (SSF)
      <app-tooltip-wrapper><span class="extra-info">(?)</span>
        <ng-template #tooltip>
          - Selects the <strong>Fixed (SSF)</strong> Economy, with fixed currency values which do not update automatically.
          <br />- Changes some <strong>presets</strong> to show more potentially interesting Rares.
          <span class="tooltip-section"><strong>Caution!</strong> Verify the Unique, Chance Base and Gear Tiers and adjust if necessary.</span>
        </ng-template>
      </app-tooltip-wrapper>
    </div>

    @if (poe1) {
      <div class="title-toggle-btn" (click)="toggleRuthless()">
        <span class="input-group-btn">
          <button class="btn toggle-btn" [class.off]="!filter.ruthless"></button>
        </span>
        Ruthless
        <app-tooltip-wrapper><span class="extra-info">(?)</span>
        <ng-template #tooltip>
            - Uses Fixed (SSF) Economy and 'Early Game' Tiering Mode.
            <br />- Uses different presets with much lower thresholds for hiding items.
            <br />- Removes Ruthless-disabled Currency and Currency Types from the UI and generated output.
            <br />- Ruthless filters cannot hide items. Instead, they are displayed using the 'Minimal' keyword, which shows small and transparent labels.
            <br />- Filters are saved with the .ruthlessfilter extension, and uploaded to your account using the 'Ruthless' type.
            <br /><br /><strong>Heads up!</strong> Ruthless support is still in beta and much less tested. Help us improve it!
          </ng-template>
      </app-tooltip-wrapper>
    </div>
    }
  </div>

  <p class="progress-preset-info extra-info limited-width">
    @if (overridePresetTooltip) {
      {{ overridePresetTooltip }}
    } @else if (poe1 && filter.progressPreset === 'presetMidMaps70' && !filter.ssf) {
      {{ presetsService.poe1progressAtlasNonSsfPresetText }}
    } @else if (filter.progressPreset) {
      {{ presetsService.levelPresetTexts[filter.progressPreset!] }}
    }
    @if (overrideTieringModesSummary) {
      <br />{{ overrideTieringModesSummary }}
    } @else if (filter.progressPreset && tieringModesSummary) {
      <br />{{ tieringModesSummary }}
      (<a class="cursor-pointer" (click)="toggleChangeTieringModes()">{{ showTieringModes ? 'Done' : 'Change' }}</a>)
    }
  </p>

  <div class="section" [class.visible]="showTieringModes && !!filter.progressPreset">
    <div class="section-divider"></div>

    <div class="preset-options-header">
      <h1 class="gradient-title">Tiering Modes</h1>
      <p class="extra-info">
        Adjust how strictly Currency{{ poe1 && !filter.ruthless ? ', Divination Cards' : '' }}, Uniques{{ poe1 ? ' and Cluster Jewels' : '' }} are tiered. You can further fine-tune this on the Tier List tabs.
      </p>
    </div>

    @if (filter.progressPreset) {
      <app-progress-tiering-modes
        [filter]="filter"
        [refreshKey]="tieringModesRefreshKey"
        (modesChanged)="refreshTieringModesSummary()" />
    }
  </div>

  <div class="section customize-styles-section" [class.visible]="filter.progressPreset">
    <div class="section-divider"></div>

    <div class="preset-options-header">
      <h1 class="gradient-title">Customize Styles</h1>
      <p class="extra-info">
        Adjust the visual style of Top-Tier Currency, Uniques and Gold, and set the default label size. Further customize this on the <a href="/cosmetic">Cosmetic</a> tab.
      </p>
    </div>

    <div class="section-row">
      <div class="title-toggle-btn" (click)="toggleChangeVisualStyle()">
        <span class="input-group-btn">
          <button class="btn toggle-btn" [class.off]="!changeVisualStyle"></button>
        </span>
        Change Top-Tier&nbsp;<strong>Label Styles</strong>
      </div>
      <div class="title-toggle-btn" (click)="toggleShowTransparentGold()">
        <span class="input-group-btn">
          <button class="btn toggle-btn" [class.off]="!showTransparentGold"></button>
        </span>
        Transparent Gold
      </div>
    </div>

    <p class="extra-info">
      (<a class="cursor-pointer" (click)="toggleCustomizeLabelStyles()">{{ showCosmeticPresets ? 'Finish Customizing' : 'Customize Label Styles' }}</a>)
    </p>

    <div class="customize-styles-preview">
      <app-drop-preview
        [initialSections]="presetsDropPreviewInitialSections"
        [randomLootSettingsOverride]="presetsDropPreviewRandomLootSettings"
        [fixedLabelLayout]="'scattered'"
        [scrollable]="false"
        [showAreaLevelInput]="true" />
    </div>

    <div class="section" [class.visible]="showCosmeticPresets">
      <div class="preset-options-header">
        <h1 class="gradient-title">Tiered label Styles</h1>
        <p class="extra-info">
          Change the visual style of Top-Tier <span class="currency-color">Currency</span>, <span class="unique-item-color">Unique</span> and <span class="normal-item-color">Chance Base</span> item labels so they stand out more. You can further customize this on the <a href="/cosmetic">Cosmetic</a> tab.
        </p>
      </div>

      <div class="cosmetic-presets-section">
        <h3>Label Colors</h3>
        <div class="cosmetic-options">
          <div class="cosmetic-option" [class.selected]="filter.cosmeticPresets.labelColor === 'default'" (click)="setLabelColor('default')">
            Default
          </div>

          <div class="cosmetic-option" [class.selected]="filter.cosmeticPresets.labelColor === 'accentNeutral'" (click)="setLabelColor('accentNeutral')">
            Accent, Neutral
          </div>

          <div class="cosmetic-option" [class.selected]="filter.cosmeticPresets.labelColor === 'accentTiered'" (click)="setLabelColor('accentTiered')">
            Accent, Tiered
          </div>
        </div>
      </div>

      <div class="cosmetic-presets-section">
        <h3>Label Sizes</h3>
        <div class="cosmetic-options">
          <div class="cosmetic-option" [class.selected]="filter.cosmeticPresets.labelSize === 'default'" (click)="setLabelSize('default')">
            Default
          </div>

          <div class="cosmetic-option" [class.selected]="filter.cosmeticPresets.labelSize === 'larger'" (click)="setLabelSize('larger')">
            Larger
          </div>

          <div class="cosmetic-option" [class.selected]="filter.cosmeticPresets.labelSize === 'veryLarge'" (click)="setLabelSize('veryLarge')">
            Increasing
          </div>
        </div>
      </div>

      @if (showStartingTierSlider) {
        <div class="cosmetic-presets-section">
          <h3>Starting Tier</h3>
          <div class="cosmetic-options label-size-slider">
            <input type="range" min="0" max="3" [value]="startingTierIndex" (input)="onStartingTierChange($event)" />
            <span class="label-size-value">{{ startingTierLabel }}</span>
          </div>
        </div>
      }

      <div class="cosmetic-presets-section">
        <h3>Default Size</h3>
        <div class="cosmetic-options label-size-slider">
          <input type="range" min="0" max="6" [value]="defaultLabelSizeIndex" (input)="onDefaultLabelSizeChange($event)" />
          <span class="label-size-value">{{ defaultLabelSizeLabel }}</span>
        </div>
      </div>
    </div>
  </div>

  <div class="section" [class.visible]="filter.progressPreset === 'presetMidMaps70' || filter.progressPreset === 'presetEndGame80' || filter.progressPreset === 'presetStrict90' || filter.progressPreset === 'presetVeryStrict95'">
    <div class="section-divider"></div>

    <div class="preset-options-header">
      <h1 class="gradient-title">Endgame Content</h1>
      <p class="extra-info">
        Select which endgame content and other mechanics you occasionally engage with. Uniques dropped by the selected content will never be hidden.
      </p>
    </div>

    <div class="preset-options preset-options-small">
      <div class="endgame-content-option"
        [class.selected]="filter.favouriteEndgameContent.includes('All')"
        (click)="toggleFavouriteEndgameContent('All')"
        (mouseenter)="showEndgameContentInfo('All')"
        (mouseleave)="hideEndgameContentInfo()">
        <div class="endgame-content-icon endgame-content-icon-stack">
          @for (icon of endgameContentPreviewIcons; track icon) {
            <img src="{{ imgPrefix }}{{ icon }}" alt="" />
          }
          <div class="endgame-content-overlay"></div>
        </div>
      </div>
      @for (c of presetsService.endgameContent; track c) {
        <div class="endgame-content-option"
          [class.selected]="!filter.favouriteEndgameContent.includes('All') && filter.favouriteEndgameContent.includes(c)"
          (click)="toggleFavouriteEndgameContent(c)"
          (mouseenter)="showEndgameContentInfo(c)"
          (mouseleave)="hideEndgameContentInfo()">
          <div class="endgame-content-icon">
            <img src="{{ imgPrefix }}{{ favouriteEndgameContentMap[c] }}" alt="" />
            <div class="endgame-content-overlay"></div>
          </div>
        </div>
      }
    </div>

    @if (presetsService.endgameBosses.length) {
      <div class="preset-options preset-options-small endgame-bosses-row">
        @for (c of presetsService.endgameBosses; track c) {
          <div class="endgame-content-option"
            [class.selected]="!filter.favouriteEndgameContent.includes('All') && filter.favouriteEndgameContent.includes(c)"
            (click)="toggleFavouriteEndgameContent(c)"
            (mouseenter)="showEndgameContentInfo(c)"
            (mouseleave)="hideEndgameContentInfo()">
            <div class="endgame-content-icon">
              <img src="{{ imgPrefix }}{{ favouriteEndgameContentMap[c] }}" alt="" />
              <div class="endgame-content-overlay"></div>
            </div>
          </div>
        }
      </div>
    }

    <p class="endgame-content-info extra-info limited-width">
      @if (!endgameContentHoveringAll && (endgameContentHoverDetail ?? endgameContentSelectedDetail); as detail) {
        <span class="endgame-content-detail">
          <span class="endgame-content-detail-line">
            <span class="endgame-content-detail-title">{{ detail.contentLine }}</span>
          </span>
          @if (detail.itemTypes.length || detail.scarabs.length) {
            <span class="endgame-content-detail-line">
              @if (detail.itemTypes.length) {
                <span class="endgame-content-detail-label">Currency types:</span>
                <span class="endgame-content-detail-chips">
                  @for (item of detail.itemTypes; track item.itemType) {
                    <span class="endgame-content-detail-chip">
                      @if (item.image) {
                        <img class="inline-image" [src]="item.image" alt="" />
                      }
                      <span class="currency-color">{{ item.itemType }}</span>
                    </span>
                  }
                </span>
                @if (detail.moreItemTypesText) {
                  <span class="endgame-content-detail-more">{{ detail.moreItemTypesText }}</span>
                }
              }
              @if (detail.scarabs.length) {
                <span class="endgame-content-detail-label" [class.endgame-content-detail-label-follow]="detail.itemTypes.length">Scarabs:</span>
                <span class="endgame-content-detail-chips" [class.endgame-content-detail-icons]="detail.scarabsIconOnly">
                  @for (scarab of detail.scarabs; track scarab.name) {
                    <span [class.endgame-content-detail-chip]="!detail.scarabsIconOnly" [class.endgame-content-detail-icon]="detail.scarabsIconOnly">
                      <app-item-label
                        [baseType]="scarab.name"
                        [image]="scarab.image"
                        [iconOnly]="detail.scarabsIconOnly"
                        [inline]="true">
                      </app-item-label>
                    </span>
                  }
                </span>
              }
            </span>
          }
          @if (detail.uniqueGroups.length) {
            <span class="endgame-content-detail-line">
              @for (group of detail.uniqueGroups; track group.label; let i = $index) {
                <span class="endgame-content-detail-boss">
                  <span class="endgame-content-detail-label" [class.endgame-content-detail-label-follow]="i > 0">{{ group.label }}</span>
                  <span class="endgame-content-detail-chips" [class.endgame-content-detail-icons]="group.iconOnly">
                    @for (unique of group.uniques; track unique.name) {
                      <span [class.endgame-content-detail-unique]="!group.iconOnly" [class.endgame-content-detail-icon]="group.iconOnly">
                        <app-item-label
                          [unique]="true"
                          [baseType]="unique.name"
                          [image]="unique.image"
                          [iconOnly]="group.iconOnly"
                          [inline]="true">
                        </app-item-label>
                      </span>
                    }
                  </span>
                </span>
              }
            </span>
          }
        </span>
      } @else if (endgameContentHoveringAll || filter.favouriteEndgameContent.includes('All')) {
        <span class="endgame-content-detail">
          <span class="endgame-content-detail-line">
            <span class="endgame-content-detail-title">{{ endgameAllLabel }}</span>
          </span>
          <span class="endgame-content-detail-line">No favourite item types</span>
          <span class="endgame-content-detail-line">{{ endgameAllBossUniquesStatusLine }}</span>
        </span>
      }
    </p>

    @if (showEndgamePresetFavouriteCurrencyToggle || showEndgamePresetPromoteScarabsToggle || showEndgamePresetCustomRulesToggle) {
      <div class="section-row">
        @if (showEndgamePresetFavouriteCurrencyToggle) {
          <div class="title-toggle-btn" (click)="toggleEndgamePresetPromoteFavouriteCurrency()">
            <span class="input-group-btn">
              <button class="btn toggle-btn" [class.off]="!filter.endgamePresetPromoteFavouriteCurrency"></button>
            </span>
            Promote selected Currency Types
            <app-tooltip-wrapper><span class="extra-info">(?)</span>
              <ng-template #tooltip>
                Promotes Currency Types related to your selected endgame content as Favourite Currency.
              </ng-template>
            </app-tooltip-wrapper>
          </div>
        }

        @if (showEndgamePresetPromoteScarabsToggle) {
          <div class="title-toggle-btn" (click)="toggleEndgamePresetPromoteScarabs()">
            <span class="input-group-btn">
              <button class="btn toggle-btn" [class.off]="!filter.endgamePresetPromoteScarabs"></button>
            </span>
            Promote Scarabs
            <app-tooltip-wrapper><span class="extra-info">(?)</span>
              <ng-template #tooltip>
                Promotes scarabs for your selected endgame content as Favourite Scarabs.
              </ng-template>
            </app-tooltip-wrapper>
          </div>
        }

        @if (showEndgamePresetCustomRulesToggle) {
          <div class="title-toggle-btn" (click)="toggleEndgamePresetEnableCustomRules()">
            <span class="input-group-btn">
              <button class="btn toggle-btn" [class.off]="!filter.endgamePresetEnableCustomRules"></button>
            </span>
            Enable Special Custom Rules
            <app-tooltip-wrapper><span class="extra-info">(?)</span>
              <ng-template #tooltip>
                Adds special custom filter rules for your selected endgame content.
                @for (entry of endgamePresetCustomRuleDescriptions; track entry.contentLabel) {
                  <br /><br /><span class="endgame-content-detail-title">{{ entry.contentLabel }}</span><br />{{ entry.description }}
                }
              </ng-template>
            </app-tooltip-wrapper>
          </div>
        }
      </div>
    }

    <div class="section-row">
      <div class="title-toggle-btn" (click)="toggleBosses()">
        <span class="input-group-btn">
          <button class="btn toggle-btn" [class.off]="!bosses"></button>
        </span>
        Show All Restricted Uniques
        <app-tooltip-wrapper><span class="extra-info">(?)</span>
          <ng-template #tooltip>
              <span class="unique-item-color">Restricted</span> Uniques are those where valuable Unique(s) for that Base Type can only be dropped by a specific boss or other endgame content.
              <br /><br />If you're not doing Endgame Bosses, you can safely hide these.
              <br /><br />On the <a href="/tier-list/uniques">Uniques Tier List</a> tab, you can see which Uniques are <span class="unique-item-color">Restricted</span>.
            </ng-template>
        </app-tooltip-wrapper>
      </div>

      @if (showFavouriteRestrictedBossUniquesToggle) {
        <div class="title-toggle-btn" (click)="toggleShowFavouriteRestrictedBossUniques()">
          <span class="input-group-btn">
            <button class="btn toggle-btn" [class.off]="!filter.showFavouriteRestrictedBossUniques"></button>
          </span>
          Show Favourite Restricted Uniques
          <app-tooltip-wrapper><span class="extra-info">(?)</span>
            <ng-template #tooltip>
              Still highlight <span class="unique-item-color">Restricted</span> boss uniques from your selected endgame content, even when other boss uniques are hidden.
            </ng-template>
          </app-tooltip-wrapper>
        </div>
      }

      @if (poe2) {
        <div class="title-toggle-btn" (click)="toggleShowCheapUniques()">
          <span class="input-group-btn">
            <button class="btn toggle-btn" [class.off]="!showCheapUniques"></button>
          </span>
          Show Low-Value Uniques
          <app-tooltip-wrapper><span class="extra-info">(?)</span>
            <ng-template #tooltip>
              Shows <span class="unique-item-color">Unique</span> items in the <span class="unique-item-color">Other</span> tier, which are worth less than {{ getOtherUniquesThreshold() }}.
              @if (filter.showSmallUniques) {
                <br /><br />Small Low-Value Uniques are still shown with a D-Tier highlight, to Disenchant.
              }
            </ng-template>
          </app-tooltip-wrapper>
        </div>
      }

      @if (poe1) {
        <div class="title-toggle-btn" (click)="toggleShippingUniques()">
          <span class="input-group-btn">
            <button class="btn toggle-btn" [class.off]="!shippingUniques"></button>
          </span>
          Show Disenchant Uniques
          <app-tooltip-wrapper><span class="extra-info">(?)</span>
            <ng-template #tooltip>
                  If you're doing Kingsmarch Shipments or using the Recombinator, the filter can highlight non-restricted <span class="unique-item-color">Unique</span> items which have a decent average and minimum <img class="inline-image" src="{{ imgPrefix }}/img/icons/dust.png" /> Thaumaturgic Dust value (with optional size discount), so you can pick them up to disenchant.
                </ng-template>
          </app-tooltip-wrapper>
        </div>
      }

      <div class="title-toggle-btn" (click)="toggleChanceBases()">
        <span class="input-group-btn">
          <button class="btn toggle-btn" [class.off]="!chanceBases"></button>
        </span>
        Show Chance Bases
        <app-tooltip-wrapper [right]="true"><span class="extra-info">(?)</span>
          <ng-template #tooltip>
              Using <span class="currency-color">Chance Orbs</span> you can try to turn <span class="normal-item-color">Normal</span> items into valuable <span class="unique-item-color">Unique</span> items.
              @if (poe1) {
                The chance for low-tier uniques is one in hundreds. For top-tier uniques it is one in tens of thousands.
              }
            </ng-template>
        </app-tooltip-wrapper>
      </div>

      @if (poe1 && !isSpecificEndgameContentSelected) {
        <div class="title-toggle-btn" (click)="toggleHeist()">
          <span class="input-group-btn">
            <button class="btn toggle-btn" [class.off]="!heist"></button>
          </span>
          Heist
          <app-tooltip-wrapper><span class="extra-info">(?)</span>
            <ng-template #tooltip>
              If you're not doing Heist, the filter can hide Rogue's Markers, Contracts and Blueprints.
            </ng-template>
          </app-tooltip-wrapper>
        </div>
        <div class="title-toggle-btn" (click)="toggleTomes()">
          <span class="input-group-btn">
            <button class="btn toggle-btn" [class.off]="!tomes"></button>
          </span>
          Forbidden Sanctum
          <app-tooltip-wrapper [right]="true"><span class="extra-info">(?)</span>
            <ng-template #tooltip>
              If you're not running The Forbidden Sanctum, the filter can hide <span class="currency-color">Forbidden Tomes</span>.
            </ng-template>
          </app-tooltip-wrapper>
        </div>
      }

    </div>

    <div class="section-row">
      @if (poe2 && (filter.progressPreset === 'presetMidMaps70' || filter.progressPreset === 'presetEndGame80' || filter.progressPreset === 'presetStrict90' || filter.progressPreset === 'presetVeryStrict95')) {
        <div class="title-toggle-btn" (click)="toggleSupportGems()">
          <span class="input-group-btn">
            <button class="btn toggle-btn" [class.off]="filter.hideUncutGems && filter.hideUncutSupportGemsBelowLevel === 5"></button>
          </span>
          Show Support Gems
          <app-tooltip-wrapper><span class="extra-info">(?)</span>
            <ng-template #tooltip>
              Shows or Highlights Level 5 <span class="currency-color">Support Gem</span>s.
            </ng-template>
          </app-tooltip-wrapper>
        </div>

        <div class="title-toggle-btn" (click)="toggleJewels()">
          <span class="input-group-btn">
            <button class="btn toggle-btn" [class.off]="!jewels"></button>
          </span>
          Show Jewels
          <app-tooltip-wrapper><span class="extra-info">(?)</span>
            <ng-template #tooltip>
              Decide whether or not you want to show (and thus pick up) all jewels.
              @if (filter.showIdentifiedJewels) { Note that our filters will automatically highlight potentially interesting <i>identified</i> jewels. }
            </ng-template>
          </app-tooltip-wrapper>
        </div>

        @if (poe2) {
          <div class="title-toggle-btn" (click)="toggleShowAllIdentifiedItems()">
            <span class="input-group-btn">
              <button class="btn toggle-btn" [class.off]="!filter.showAllIdentifiedItems"></button>
            </span>
            Show All Identified Items
            <app-tooltip-wrapper [right]="true"><span class="extra-info">(?)</span>
              <ng-template #tooltip>
                Always show all identified items dropped on the ground. Fine for most players. Reduces Filter Output size.
                <br /><br />Disable this if you regularly identify items and then throw them on the ground to only keep potentially good ones. Greatly increases Filter Output size.
              </ng-template>
            </app-tooltip-wrapper>
          </div>
        }
      }
      @if (poe1 && (filter.progressPreset === 'presetStrict90' || filter.progressPreset === 'presetVeryStrict95')) {
        <div class="break-on-small">
          Map Types
          <app-tooltip-wrapper [right]="true"><span class="extra-info">(?)</span>
            <ng-template #tooltip>
              Specify which types of endgame maps you want to see.
            </ng-template>
          </app-tooltip-wrapper>

          <app-autocomplete class="inline" style="height: 30px"
            [allLabel]="'None'"
            [options]="mapTypeOptions"
            [disabled]="!filter.dynamicWaystones"
            [showOptions]="true"
            [selectedValues]="selectedMapTypes"
            (selectedValuesChange)="selectedMapTypesChanged($event)"
          ></app-autocomplete>
        </div>
      } @else if (poe1 && (filter.progressPreset !== 'presetStrict90' && filter.progressPreset !== 'presetVeryStrict95')) {
        <div style="height: 30px">
          Current Map Level
          <app-tooltip-wrapper [right]="true"><span class="extra-info">(?)</span>
            <ng-template #tooltip>
              This will highlight this selection of maps as B-Tier.
            </ng-template>
          </app-tooltip-wrapper>
          <input type="number" placeholder="min" maxlength="2" pattern="[0-9.,]*" inputmode="numeric" step="any" class="form-control minmax inline"
              [disabled]="!filter.dynamicWaystones" [(ngModel)]="filter.dynamicWaystonesLevel" (keyup)="daynamicWaystoneLevelChanged()">
        </div>
      }
    </div>
  </div>

  @if (poe1 && (filter.progressPreset === 'presetMidMaps70' || filter.progressPreset === 'presetEndGame80' || filter.progressPreset === 'presetStrict90')) {
    <div class="section-row">
      <div class="title-toggle-btn" (click)="toggleHideCurrency()">
        <span class="input-group-btn">
          <button class="btn toggle-btn" [class.off]="!filter.hideCurrency"></button>
        </span>
        Hide Currency
        <app-tooltip-wrapper><span class="extra-info">(?)</span>
          <ng-template #tooltip>
            Always hides the selected currencies when they drop unstacked, regardless of their market value. On the <a href="/tier-list/currency">Currency Tier List</a> tab you can specify in detail which currency belongs to which tier.
          </ng-template>
        </app-tooltip-wrapper>
      </div>
      <div>
        <app-autocomplete class="inline" style="width: 215px"
          [allLabel]="'None'"
          [options]="cheapCurrencyWithImages"
          [disabled]="!filter.hideCurrency"
          [showOptions]="true"
          [selectedValues]="filter.hideCurrencyTypes"
          (selectedValuesChange)="filter.hideCurrencyTypes = $event; rebuildCurrencyTiers()">
        </app-autocomplete>
      </div>
    </div>
  }

  @if (poe2 && filter.progressPreset === 'presetVeryStrict95') {
    <div class="section-row">
      <div class="title-toggle-btn" (click)="toggleHideETier()">
        <span class="input-group-btn">
          <button class="btn toggle-btn" [class.off]="!hideETier"></button>
        </span>
        Hide Chaos Orbs and similar
        <app-tooltip-wrapper><span class="extra-info">(?)</span>
          <ng-template #tooltip>
            Hides E-Tier currency, which is worth between 0.75 and 2.5 <span class="currency-color">Chaos Orbs</span>, such as
            @for(currency of eTierCurrency; track currency) { <span class="separated-by-comma"><span class="currency-color">{{ currency }}</span></span> }.
            Alternatively, on the <a href="/tier-list/currency">Currency Tier List</a> tab, you can create Custom Tiering Modes, or manually set the Tier of individual currency.
          </ng-template>
        </app-tooltip-wrapper>
      </div>

      <div class="title-toggle-btn" (click)="toggleLowValueTablets()">
        <span class="input-group-btn">
          <button class="btn toggle-btn" [class.off]="!hideLowValueTablets"></button>
        </span>
        Hide Low Value Tablets
        <app-tooltip-wrapper [right]="true"><span class="extra-info">(?)</span>
          <ng-template #tooltip>
            Hide Irradiated and Overseer Tablets.
          </ng-template>
        </app-tooltip-wrapper>
      </div>
    </div>
  }

  <div class="section" [class.visible]="filter.progressPreset">
    <div class="section-divider"></div>

    <div class="preset-options-header">
      <h1 class="gradient-title">Select your class</h1>
      <p class="extra-info">
        Shows more of the Weapons and Defence types relevant to your class.
        @if (filter.ssf) {
          It will also show more potentially interesting <span class="unique-item-color">Unique</span> items used by your class.
        }
      </p>
    </div>

    <div class="preset-options class-preset-options">
      @for (cls of classPresetOptions; track cls.key) {
        <div class="preset-option class-option" [class.selected]="filter.classPreset === cls.key" (click)="setClassPreset(cls.key)" (mouseenter)="showClassPresetTooltip(cls.key)" (mouseleave)="hideClassPresetTooltip()">
          <h3>{{ cls.key }}</h3>
          <div class="preset-image-wrap">
            <img [src]="cls.img"/>
            <button type="button" class="preset-preview-btn" title="Preview changes" (click)="previewClassPreset(cls.key); $event.stopPropagation()">
              <span class="icon icon-zoom-in"></span>
            </button>
          </div>
        </div>
      }
    </div>

    <p class="class-preset-info extra-info limited-width">
      @if (overrideClassPresetTooltip) {
        {{ overrideClassPresetTooltip }}
      } @else if (filter.classPreset) {
        {{ classPresetTexts[filter.classPreset!] }}
      }
    </p>
  </div>

  <div class="section" [class.visible]="filter.classPreset">
    <div class="section-divider"></div>

    <div class="preset-options-header">
      <h1 class="gradient-title">{{ filter.showLevelingGear ? 'Leveling Weapon & Defence Types' : 'Weapon & Defence Types' }}</h1>
      <p class="extra-info">
        Show Gear relevant to your character.
        @if (filter.showLevelingGear) {
          Only the best items will be shown for the Area you're in. {{ poe2 ? 'Magic and ' : '' }} Rare items will remain visible for longer.
          @if (poe2 && filter.showQualityArmour) {
            <br />Quality or Socketed gear will still be shown, to salvage.
          }
        } @else {
          Special Variants of these items, {{ poe2 ? 'like Unidentified Tiered Gear or Exceptional Gear' : 'like Gear with Memory Strands'}}, will be highlighted automatically.
        }
      </p>
    </div>

    <div class="section-row">
      <div class="option-text-align-right">
        @if (filter.showLevelingGear) {
          Leveling Weapons
          <app-tooltip-wrapper><span class="extra-info">(?)</span>
            <ng-template #tooltip>
              @if (poe2 && filter.showLevelingGearWeapons) {
                Starter weapons in The Riverbank are never hidden.
              }
              @if (poe1) {
                Shows gear which is potentially useful while leveling through the campaign; optionally of the specified Weapon Class or Armour Defence Type, and optionally with auto-scaling linked sockets. On the <a href="/quick-filter">Quick Filters</a> tab, you can further specify if and how different types of gear are shown, and how large the level difference between the Area Level and the Drop Level of the item can be.
                <br /><br />

                @if (filter.showLevelingGearNormal) {
                  <span class="normal-item-color">Normal</span> gear is shown with a Drop Level of maximum <strong>{{filter.showLevelingGearNormalLevelDifference}}</strong> levels below the Area Level.
                }

                @if (filter.showLevelingGearMagic) {
                  <span class="magic-item-color">Magic</span> gear is shown with a Drop Level of maximum <strong>{{filter.showLevelingGearMagicLevelDifference}}</strong> levels below the Area Level.
                }

                @if (filter.showLevelingGearRare) {
                  <span class="rare-item-color">Rare</span> gear is shown with a Drop Level of maximum <strong>{{filter.showLevelingGearRareLevelDifference}}</strong> levels below the Area Level.
                }

                @if (filter.showLevelingGearExcellentSocketed) {
                  @if (filter.showLevelingGearExcellentSocketedDifference) {
                    <br /><br />Socketed gear with the maximum number of links that can drop at the current Area Level is shown with a Drop Level of maximum <strong>{{ filter.showLevelingGearExcellentSocketedDifference }}</strong> levels below the Area Level.
                  } @else {
                    <br /><br />Socketed gear with the maximum number of links that can drop at the current Area Level is always shown.
                  }
                }
                @if (filter.showLevelingGearOtherSocketed) {
                  @if (filter.showLevelingGearOtherSocketedLevelDifference) {
                    <br /><br />Socketed gear with one less than the maximum number of links that can drop at the current Area Level is shown with a Drop Level of maximum <strong>{{ filter.showLevelingGearOtherSocketedLevelDifference }}</strong> levels below the Area Level.
                  } @else {
                    <br /><br />Socketed gear with one less than the maximum number of links that can drop at the current Area Level is always shown.
                  }
                }

                <br /><br />When enabled, on the <a href="/tier-list/gear">Gear Tier List</a> tab you can simulate which items would be shown as <span class="normal-item-color">Normal</span>, <span class="magic-item-color">Magic</span>, <span class="rare-item-color">Rare</span>, with linked sockets, or potentially Hidden.
                <br /><br />In the first 5 areas, all <span class="rare-item-color">Rares</span> of any type are shown.
              }
            </ng-template>
          </app-tooltip-wrapper>
        } @else {
          <div>Weapon Types</div>
        }

        <app-autocomplete class="inline" style="width: 215px"
          [options]="weaponClasses"
          [showOptions]="true"
          [selectedValues]="filter.showLevelingGearWeaponClasses"
          (selectedValuesChange)="levelingWeaponClassesChanged($event)"
        ></app-autocomplete>
      </div>

      <div class="option-text-align-left">
        <div>Defence Types</div>

        <app-autocomplete class="inline" style="width: 215px"
          [options]="defenceTypes"
          [showOptions]="true"
          [selectedValues]="filter.showLevelingGearArmourDefenceTypes"
          (selectedValuesChange)="levelingArmourDefenceTypesChanged($event)"
        ></app-autocomplete>
      </div>

      @if (poe1) {
        <div>
          <div class="title-toggle-btn" (click)="toggleSearchedGems()">
            <span class="input-group-btn">
              <button class="btn toggle-btn" [class.off]="!filter.showSearchedGems"></button>
            </span>
            Favourite Gems
            <app-tooltip-wrapper [right]="true"><span class="extra-info">(?)</span>
              <ng-template #tooltip>
                This will highlight the <span class="gem-color">Gems</span> you are looking for for your build. You can bulk import them by copy/pasting the text from your (Path of Building or other) build guide.
                <br /><br />Setting these, the '<strong>Quest & Gem Tracker</strong>' section on the <a href="/extra">Extra</a> tab will show you which quests reward or unlock these gems.
              </ng-template>
            </app-tooltip-wrapper>
          </div>

          <button type="button" class="btn btn-default" (click)="bulkImportDialogOpen = true" style="width: 40px; min-width: 40px; margin-left: 12px; border:none;" title="Bulk Import">
            <span class="icon icon-download"></span>
          </button>

          @if (bulkImportDialogOpen) {
            <app-bulk-import-gems-dialog class="dialog-top" (close)="bulkImportDialogOpen = false" (import)="importGems($event)"></app-bulk-import-gems-dialog>
          }

          <app-autocomplete class="inline"
            [allLabel]="'None'"
            [options]="gemTypes"
            [disabled]="false"
            [showOptions]="true"
            [selectedValues]="filter.showSearchedGemTypes"
            (selectedValuesChange)="filter.showSearchedGemTypes = $event; updateFilter();"></app-autocomplete>
        </div>
      }
    </div>

    @if (poe2) {
      @let quiverSelected = hasQuiverSelected();
      @let casterWeaponSelected = hasCasterWeaponSelected();
      @if ((quiverSelected || casterWeaponSelected) && (filter.progressPreset === 'presetEarlyCampaign1' || filter.progressPreset === 'presetMidCampaign30')) {
        <div class="section-row">
          <div class="title-toggle-btn" (click)="toggleShowLevelingGearIgnoreLevelDifferenceForSpecialWeaponTypes()">
            <span class="input-group-btn">
              <button class="btn toggle-btn" [class.off]="!filter.showLevelingGearIgnoreLevelDifferenceForSpecialWeaponTypes"></button>
            </span>
            Show all
            @if (quiverSelected && casterWeaponSelected) {
              Caster Weapons & Quivers
            } @else if (quiverSelected) {
              Quivers
            } @else if (casterWeaponSelected) {
              Caster Weapons
            }
            regardless of Drop Level
            <app-tooltip-wrapper [right]="true"><span class="extra-info">(?)</span>
              <ng-template #tooltip>
                The Base Types of Caster Weapons (Wands, Sceptres, Staves) and Quivers don't have inherent damage or defence stats which scale with their Drop Level, so they remain relevant longer — even until endgame.

                <br /><br />Enabling this will not hide them based on the level difference between their Drop Level and the Area Level.
              </ng-template>
            </app-tooltip-wrapper>
          </div>
        </div>
      }
    }

    @if (filter.progressPreset === 'presetEarlyCampaign1' || filter.progressPreset === 'presetMidCampaign30' || filter.progressPreset === 'presetLateCampaignEarlyMaps60' || (filter.progressPreset === 'presetMidMaps70' && poe2)) {
      <div class="section-row">
        @if (poe2) {
          <div>
            Gear to Sell or Disenchant
            <app-tooltip-wrapper [right]="true"><span class="extra-info">(?)</span>
              <ng-template #tooltip>
                Shows <span class="magic-item-color">Magic</span> and/or <span class="rare-item-color">Rare</span> Gear which can be sold at an NPC vendor for <span class="currency-color">Gold</span>, or Disenchanted.
                <br /><br /><span class="rare-item-color">Rare</span> items are Disenchanted into 1 (or 2, for 6 mods) <span class="currency-color">Regal Shard</span>. <span class="magic-item-color">Magic</span> items are Disenchanted into 1 <span class="currency-color">Transmutation Shard</span>.
                <br /><br />By default, these items are shown with a transparent style. You can change this on the <a href="/quick-filter">Quick Filters</a> tab.
                <br /><br />
                <app-rule-preview [scrollable]="false" [compact]="true" [rules]="[vendoringRareStyle, vendoringMagicStyle]"></app-rule-preview>
              </ng-template>
            </app-tooltip-wrapper>
          </div>
        }
        <div>
          @if (poe1) {
            <div class="title-toggle-btn" (click)="toggleShowVendoringRares()">
              <span class="input-group-btn">
                <button class="btn toggle-btn" [class.off]="!filter.showVendoringRares"></button>
              </span>
              Vendoring Rares
              <app-tooltip-wrapper [right]="true"><span class="extra-info">(?)</span>
                <ng-template #tooltip>
                  Shows <span class="rare-item-color">Rare</span> Gear which can be sold at an NPC vendor for 5x <span class="currency-color">Transmutation Shard</span>s (unidentified) or <span class="currency-color">Alteration Shard</span>s (identified, amount depending on mods).
                  <br /><br />By default these items will be shown <strong>transparently</strong>, to distinguish them from the the above-selected Rare Leveling Gear. You can change the way they're displayed on the Quick Filters tab.
                </ng-template>
              </app-tooltip-wrapper>
            </div>
          } @else {
            <div class="title-toggle-btn" (click)="toggleShowVendoringRares()">
              <span class="input-group-btn">
                <button class="btn toggle-btn" [class.off]="!filter.showVendoringRares"></button>
              </span>
              Rare
            </div>
          }

          <app-select-gear-size [inline]="true"
            [allowedSizes]="[GearSizeType.All, GearSizeType.MediumOnly, GearSizeType.TwoByTwoOnly]"
            [disabled]="!filter.showVendoringRares"
            [(ngModel)]="filter.showVendoringRaresSize"
            (selectedValueChange)="updateFilter()"></app-select-gear-size>
        </div>

        <div>
          @if (poe2) {
            <div class="title-toggle-btn" (click)="toggleShowVendoringMagics()">
              <span class="input-group-btn">
                <button class="btn toggle-btn" [class.off]="!filter.showVendoringMagics"></button>
              </span>
              Magic
            </div>
          } @else {
            <div class="title-toggle-btn" (click)="toggleShowVendoringMagics()">
              <span class="input-group-btn">
                <button class="btn toggle-btn" [class.off]="!filter.showVendoringMagics"></button>
              </span>
              Vendoring Magics
              <app-tooltip-wrapper [right]="true"><span class="extra-info">(?)</span>
                <ng-template #tooltip>
                  Additionally shows <span class="magic-item-color">Magic</span> Gear until the designated Area Level, which can be sold at an NPC vendor for 2x <span class="currency-color">Transmutation Shard</span>s (unidentified) or <span class="currency-color">Alteration Shard</span>s (identified, amount depending on mods).
                  <br /><br />By default these items will be shown <strong>transparently</strong>, to distinguish them from the the above-selected Rare Leveling Gear. You can change the way they're displayed on the Quick Filters tab.
                </ng-template>
              </app-tooltip-wrapper>
            </div>
          }

          <app-select-gear-size [inline]="true" style="margin-right: 12px;"
            [allowedSizes]="[GearSizeType.All, GearSizeType.MediumOnly, GearSizeType.TwoByTwoOnly]"
            [disabled]="!filter.showVendoringMagics"
            [(ngModel)]="filter.showVendoringMagicsSize"
            (selectedValueChange)="updateFilter()"></app-select-gear-size>

          @if (poe1) {
            <span style="margin-right: 12px;">Until Area</span>
            <input type="number" placeholder="20" minlength="0" maxlength="6" pattern="[RGBWX]{1,6}" class="form-control minmax preset-number-input" style="margin-right: 12px;"
              [disabled]="!filter.showVendoringMagics" [(ngModel)]="filter.showVendoringMagicsUntilAreaLevel"
              (keydown)="socketGroupKeyDown($event)" (change)="updateFilter()">
          }
        </div>
      </div>
    }
  </div>

  <div class="section" [class.visible]="filter.classPreset">
    <div class="section-divider"></div>

    <div class="preset-options-header">
      <h1 class="gradient-title">Save, Sync or Customize further</h1>
      <p class="extra-info">
        Further fine-tune your filter using the <a href="/quick-filters">Quick Filters</a> tab. Review and tweak the tiers of
        @if (poe1) {
          Currency, Divination Cards, Uniques, Chance Bases, Gear and Mods
        } @else {
          Currency, Uniques, Chance Bases and Gear
        }
        on the <a href="/tier-list">Tier Lists</a> tab. Create fine-grained <a href="/custom-rules">Custom Rules</a>. Change how items will be displayed on the <a href="/cosmetic">Cosmetic</a> tab. Finally, <a href="/preview">Preview</a> how your filter will look in-game.
      </p>
      <p class="extra-info">
        (<a class="cursor-pointer" (click)="toggleInstallationInfo()">{{ installationInfo ? 'Hide' : 'More' }} info about how to install the filter</a>)
      </p>
    </div>
  </div>

  <div class="section" [class.visible]="installationInfo">
    <div class="section-divider"></div>

    <div class="preset-options-header">
      <h1 class="gradient-title">How to Install</h1>

      <p class="extra-info">
        Once you're happy with your settings, you have several options. One is to <span class="extra-info-highlight">Log in</span> and <span class="extra-info-highlight">Sync</span> your filter directly to the game. This is the only option for Console and GeForce Now users.
        @if (supportsFileSystemAccess) {
          On PC, click <span class="extra-info-highlight">Select game folder</span> once to choose your <span class="code nowrap">My Games\{{ gameName }}</span> folder (<span class="code nowrap">%userprofile%\Documents\My Games\{{ gameName }}</span>). From then on, <span class="extra-info-highlight">Save to file</span> writes the active filter there automatically as a <span class="code">.filter</span> file named after the filter. @if (poe1) { (<span class="code">.ruthlessfilter</span> in Ruthless mode.) } Your browser will ask for permission the first time; in Chrome you can choose <span class="extra-info-highlight">Allow on every visit</span> so later saves are seamless
        } @else {
          On PC, you can also <span class="extra-info-highlight">Download</span> the filter file, and put it in the correct folder
          <span>(<a [href]="null" (click)="windowsMoreInfo = !windowsMoreInfo; $event.preventDefault(); $event.stopPropagation()">{{ windowsMoreInfo ? 'Less' : 'More' }} info</a>)</span>.
        }
      </p>
      @if (windowsMoreInfo) {
        <p class="extra-info">
          On Windows this is <span style="font-weight: bolder; white-space: nowrap;">%userprofile%\Documents\My Games\{{ gameName }}</span> <a style="margin-left: 6px; text-decoration: none !important;" [href]="null" (click)="copyGameFolderLinkToClipboard($event)"><span class="icon-copy"></span></a>. You can open the correct folder from the Game's Settings Menu under 'Game'.
          <br />Pro tip: Right-click the folder in Windows Explorer and select 'Pin to Quick Access'.
        </p>
      }
      <p class="extra-info">
        Regardless of whether you saved to file, downloaded the file, or synced it to the game, you still have to <span class="extra-info-highlight">load</span> it. In the game, select the filter from the dropdown menu in the 'Game' Settings Menu (the filter synced to your account will show up as <span style="color: cyan">{{ defaultFilterName }}</span>). The game will save it for future use, and load it on startup.
      </p>
      <p class="extra-info">
        You can read more on <span class="extra-info-highlight">updating, sharing, importing or managing multiple filters</span> by scrolling down to the '<a class="cursor-pointer" (click)="scrollToHowToUseThisWebsite()">How To Use This Website</a>' section below.
      </p>
    </div>
  </div>

</div>
